<template>
    <f-dialog
        :title       = "$t('img.upload.title')"
        v-model      = "show"
        :width       = "520"
        :store-label = "finished ? $t('common.close') : $t('img.upload.store')"
        :show-save   = "finished || (pending.length > 0 && !running)"
        :show-cancel = "!finished"
        @cancel      = "onClose"
        @store       = "finished ? onClose() : onUpload()"
    >
        <template v-slot:content>
            <div class="mb-2">
                {{ $t('img.upload.folder') }}: <b>{{ folderLabel }}</b>
            </div>

            <v-file-input
                v-model     = "selected"
                :label      = "$t('img.upload.select')"
                multiple
                chips
                small-chips
                :accept     = "accept"
                :disabled   = "running"
                :show-size  = "true"
                hide-details
                @change     = "onSelect"
            />

            <v-row dense v-for="item in items" :key="item.key" class="upload-item">
                <v-col class="upload-name">{{ item.file.name }}</v-col>
                <v-col class="upload-state" :class="{'error--text': item.state === 'FAILED'}">
                    {{ $t('img.upload.state.' + item.state) }}
                    <span v-if="item.reason"> ({{ item.reason }})</span>
                </v-col>
            </v-row>
            <div class="mb-6"></div>
        </template>
    </f-dialog>
</template>

<script>

import FDialog from "@/modules/core/components/utils/FDialog.vue";
import Img from "@/modules/img/components/service/Img";

const MAX_SIZE = 10 * 1024 * 1024;
const POLL_INTERVAL = 1500;
const POLL_ATTEMPTS = 60;

export default {
    name: "DImageUpload",

    components: {
        FDialog
    },

    props: {
        value: {

        },

        site: {

        },

        /** The media service folder to upload into (see Folders.js). */
        folder: {

        },

        folderLabel: {
            default: ""
        }
    },

    data: () => ({
        show: false,
        selected: [],
        items: [],
        running: false,
        uploaded: false,
        // images and PDF, the media service checks the content
        accept: "image/jpeg,image/png,image/gif,image/tiff,image/webp,image/heic,image/heif,application/pdf",
    }),

    computed: {
        pending () {
            return this.items.filter(item => item.state === "WAITING");
        },

        // every file is done or failed: the dialog only offers to close
        finished () {
            return this.items.length > 0 && !this.running
                && this.items.every(item => item.state === "DONE" || item.state === "FAILED");
        }
    },

    watch: {
        value () {
            this.show = this.value;
            if (this.show) {
                this.selected = [];
                this.items = [];
                this.running = false;
                this.uploaded = false;
            }
        }
    },

    mounted () {
        this.show = this.value;
    },

    methods: {
        onSelect (files) {
            this.items = (files || []).map((file, index) => {
                const tooLarge = file.size > MAX_SIZE;
                return {
                    key   : index + "-" + file.name,
                    file,
                    state : tooLarge ? "FAILED" : "WAITING",
                    reason: tooLarge ? this.$t("img.upload.toolarge") : null,
                };
            });
        },

        onClose () {
            if (this.uploaded) {
                this.$emit("uploaded");
            }
            this.$emit("input", false);
        },

        async onUpload () {
            this.running = true;
            const folderId = this.folder.sources[0].id;
            const waiting = [];
            for (const item of this.pending) {
                item.state = "UPLOADING";
                try {
                    const uploadId = await Img.upload(this.site.id, folderId, item.file);
                    item.state = "IN_PROGRESS";
                    waiting.push(this.waitFor(item, uploadId));
                } catch (response) {
                    this.fail(item, response && response.status);
                }
            }

            await Promise.all(waiting);
            this.running = false;
        },

        // The media service processes an upload after accepting it; wait for the outcome.
        async waitFor (item, uploadId) {
            for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
                await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL));
                try {
                    const state = await Img.uploadState(uploadId);
                    if (state.status === "DONE") {
                        item.state = "DONE";
                        this.uploaded = true;
                        return;
                    }
                    if (state.status === "FAILED") {
                        item.state = "FAILED";
                        item.reason = state.reason || null;
                        return;
                    }
                } catch (e) {
                    // try again
                }
            }

            item.state = "FAILED";
            item.reason = this.$t("img.upload.timeout");
        },

        fail (item, status) {
            item.state = "FAILED";
            item.reason = status === 413 ? this.$t("img.upload.toolarge")
                : status === 415 ? this.$t("img.upload.unsupported")
                    : null;
        }
    }
}
</script>

<style scoped>
.upload-item {
    font-size: 14px;
}

.upload-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.upload-state {
    flex-grow: 0;
    white-space: nowrap;
}
</style>
